import { useEffect } from 'react';
import useEditorStore from '../stores/useEditorStore';
import {
  TEXT_SHORTCUTS, EDIT_SHORTCUTS, CANVAS_SHORTCUTS, TOOL_SHORTCUTS, TABLE_SHORTCUTS, findAction,
} from '../constants/keyboardShortcuts';
import { mergeCells, unmergeCells, getCellMergeInfo } from '../utils/tableMergeUtils';
import { highlightTableCells } from '../utils/tableUtils';
import { copyCustomProps } from '../utils/clipboardUtils';

/**
 * 텍스트 객체 추출 (텍스트 or 텍스트박스 그룹 내 텍스트)
 */
const getTextObj = (obj) => {
  if (!obj) return null;
  if (obj.type === 'textbox' || obj.type === 'i-text' || obj.type === 'text') return obj;
  if (obj.type === 'group' && obj._isTextBoxGroup) {
    return obj.getObjects().find(o => o.type === 'textbox' || o.type === 'i-text');
  }
  return null;
};

/**
 * 텍스트 서식 액션 처리
 */
const handleTextAction = (canvas, action, activeObj) => {
  const store = useEditorStore.getState();

  if (action === 'FONT_SIZE_UP' || action === 'FONT_SIZE_DOWN') {
    const textObj = getTextObj(activeObj);
    if (!textObj) return;
    const step = 2;
    const newSize = action === 'FONT_SIZE_UP'
      ? Math.min(textObj.fontSize + step, 500)
      : Math.max(textObj.fontSize - step, 6);
    textObj.set('fontSize', newSize);
    canvas.renderAll();
    store.syncLayersFromCanvas();
    store.pushHistory();
    store.setTextSettings({ ...store.textSettings, fontSize: newSize });
    return;
  }

  if (action === 'COPY_FORMAT') {
    const textObj = getTextObj(activeObj);
    if (!textObj) return;
    window._formatClipboard = {
      fontFamily: textObj.fontFamily,
      fontSize: textObj.fontSize,
      fontWeight: textObj.fontWeight,
      fontStyle: textObj.fontStyle,
      underline: textObj.underline,
      fill: textObj.fill,
      textAlign: textObj.textAlign,
      lineHeight: textObj.lineHeight,
      charSpacing: textObj.charSpacing,
    };
    return;
  }

  if (action === 'PASTE_FORMAT') {
    const fmt = window._formatClipboard;
    if (!fmt) return;
    const textObj = getTextObj(activeObj);
    if (!textObj) return;
    textObj.set({ ...fmt });
    canvas.renderAll();
    store.syncLayersFromCanvas();
    store.pushHistory();
  }
};

/**
 * 테이블 셀 네비게이션: 다음/이전 셀 계산
 * 병합 셀(gridSpan/rowSpan)을 건너뛰고, 실제 존재하는 셀만 탐색
 */
const getNextTableCell = (tableGroup, currentRow, currentCol, direction) => {
  const children = tableGroup.getObjects();

  // 실제 존재하는 셀 목록을 (row, col) 순으로 정렬
  const cellSet = new Set();
  children.forEach(ch => {
    if (ch._isTableCell) {
      cellSet.add(`${ch._cellRow},${ch._cellCol}`);
    }
  });

  const cellList = Array.from(cellSet).map(key => {
    const [r, c] = key.split(',').map(Number);
    return { row: r, col: c };
  }).sort((a, b) => a.row !== b.row ? a.row - b.row : a.col - b.col);

  const currentIdx = cellList.findIndex(c => c.row === currentRow && c.col === currentCol);
  if (currentIdx === -1) return cellList[0] || null;

  if (direction === 'next') {
    return currentIdx < cellList.length - 1 ? cellList[currentIdx + 1] : cellList[0];
  } else {
    return currentIdx > 0 ? cellList[currentIdx - 1] : cellList[cellList.length - 1];
  }
};

/**
 * 테이블 셀로 이동하고 텍스트 편집 시작
 */
const navigateToTableCell = (canvas, tableGroup, targetCell) => {
  if (!targetCell) return;
  const store = useEditorStore.getState();

  // 현재 편집 중인 텍스트 종료
  const activeObj = canvas.getActiveObject();
  if (activeObj?.isEditing) {
    activeObj.exitEditing();
  }

  // 셀 선택 업데이트
  store.setSelectedTableCells([targetCell]);
  store.setSelectedTableGroup(tableGroup);

  // 하이라이트 업데이트
  highlightTableCells(tableGroup, [targetCell]);

  // 대상 셀의 텍스트 객체 찾아서 편집 시작
  const textChild = tableGroup.getObjects().find(
    ch => ch._isTableCellText && ch._cellRow === targetCell.row && ch._cellCol === targetCell.col
  );
  if (textChild) {
    canvas.setActiveObject(textChild);
    textChild.enterEditing();
    // 텍스트 전체 선택
    textChild.selectAll();
  }
  canvas.renderAll();
};

/**
 * 테이블 액션 처리
 */
const handleTableAction = (canvas, action) => {
  const store = useEditorStore.getState();
  const { selectedTableCells, selectedTableGroup } = store;

  if (!selectedTableGroup) return false;

  if (action === 'TABLE_NEXT_CELL' || action === 'TABLE_PREV_CELL') {
    const currentCell = selectedTableCells[selectedTableCells.length - 1]
      || selectedTableCells[0];
    if (!currentCell) return false;

    const direction = action === 'TABLE_NEXT_CELL' ? 'next' : 'prev';
    const targetCell = getNextTableCell(selectedTableGroup, currentCell.row, currentCell.col, direction);
    navigateToTableCell(canvas, selectedTableGroup, targetCell);
    return true;
  }

  if (action === 'TABLE_MERGE') {
    if (selectedTableCells.length < 2) return false;
    const success = mergeCells(selectedTableGroup, selectedTableCells, canvas);
    if (success) {
      store.clearTableCellSelection();
      store.pushHistory();
    }
    return true;
  }

  if (action === 'TABLE_UNMERGE') {
    if (selectedTableCells.length !== 1) return false;
    const { row, col } = selectedTableCells[0];
    const mergeInfo = getCellMergeInfo(selectedTableGroup, row, col);
    if (!mergeInfo.isMerged) return false;
    const success = unmergeCells(selectedTableGroup, row, col, canvas);
    if (success) {
      store.clearTableCellSelection();
      store.pushHistory();
    }
    return true;
  }

  return false;
};

/**
 * 키보드 단축키 훅 (중앙 관리 파일 기반)
 */
const useKeyboardShortcuts = (canvas) => {
  useEffect(() => {
    if (!canvas) return;

    const handleKeyDown = async (e) => {
      // Space는 패닝 훅에서 처리
      if (e.key === ' ') return;

      // 텍스트 편집 중: 텍스트 서식 단축키만 허용
      const editingObj = canvas.getActiveObject();
      const isTextEditing = editingObj?.isEditing;

      // input/textarea/select에 포커스 시 Ctrl 조합만 허용
      const activeEl = document.activeElement;
      const isInputFocused = activeEl && (
        activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA' || activeEl.tagName === 'SELECT'
      );
      if (isInputFocused && !e.ctrlKey && !e.metaKey) return;

      const store = useEditorStore.getState();

      // 테이블 편집 모드: Tab/Shift+Tab 셀 이동 & Ctrl+M 병합/해제 우선 처리
      if (store.selectedTableGroup) {
        const tableAction = findAction(e, [TABLE_SHORTCUTS]);
        if (tableAction) {
          e.preventDefault();
          handleTableAction(canvas, tableAction);
          return;
        }
      }

      // 텍스트 편집 중: 서식 단축키만
      if (isTextEditing) {
        const textAction = findAction(e, [TEXT_SHORTCUTS]);
        if (textAction) {
          e.preventDefault();
          handleTextAction(canvas, textAction, editingObj);
        }
        return;
      }

      // 우선순위: TEXT > EDIT > CANVAS > TOOL
      const action = findAction(e, [TEXT_SHORTCUTS, EDIT_SHORTCUTS, CANVAS_SHORTCUTS, TOOL_SHORTCUTS]);
      if (!action) return;

      e.preventDefault();

      // 캔버스 제어
      if (action === 'ZOOM_IN') { store.setZoom(Math.min(store.zoom + 0.05, 2)); return; }
      if (action === 'ZOOM_OUT') { store.setZoom(Math.max(store.zoom - 0.05, 0.1)); return; }

      // 도구 전환
      const toolMap = {
        TOOL_SELECT: 'select', TOOL_LINE: 'line', TOOL_TEXT: 'text',
        TOOL_SHAPE: 'shape', TOOL_TABLE: 'table', TOOL_CHART: 'chart',
        TOOL_EYEDROPPER: 'eyedropper',
      };
      if (toolMap[action]) { store.setActiveTool(toolMap[action]); return; }

      // 편집
      if (action === 'UNDO') { store.undo(); return; }
      if (action === 'REDO') { store.redo(); return; }
      if (action === 'GROUP') { store.groupObjects(); return; }
      if (action === 'UNGROUP') { store.ungroupObjects(); return; }
      if (action === 'SAVE') {
        document.querySelector('[title="저장 (Ctrl+S)"]')?.click();
        return;
      }
      if (action === 'DELETE') {
        const activeObjects = canvas.getActiveObjects();
        if (activeObjects.length > 0) {
          activeObjects.forEach(obj => canvas.remove(obj));
          canvas.discardActiveObject();
          canvas.renderAll();
          store.pushHistory();
        }
        return;
      }
      if (action === 'COPY') {
        const activeObject = canvas.getActiveObject();
        if (activeObject) {
          try {
            const cloned = await activeObject.clone();
            copyCustomProps(activeObject, cloned);
            window._clipboard = cloned;
            window._clipboardSource = activeObject; // 원본 참조 보관 (2차 clone 대비)
          } catch (err) { console.error('Clone error:', err); }
        }
        return;
      }
      if (action === 'PASTE') {
        if (window._clipboard) {
          try {
            const cloned = await window._clipboard.clone();
            // clipboard→cloned으로 커스텀 속성 전달
            copyCustomProps(window._clipboard, cloned);
            cloned.set({ left: cloned.left + 20, top: cloned.top + 20, id: `${cloned.type}-${Date.now()}` });
            canvas.add(cloned);
            canvas.setActiveObject(cloned);
            canvas.renderAll();
            store.pushHistory();
          } catch (err) { console.error('Paste error:', err); }
        }
        return;
      }

      // 방향키 이동
      if (action.startsWith('NUDGE_')) {
        const activeObjects = canvas.getActiveObjects();
        if (activeObjects.length === 0) return;
        const amount = action.includes('_10') ? 10 : 1;
        activeObjects.forEach(obj => {
          if (action.includes('UP')) obj.set('top', obj.top - amount);
          if (action.includes('DOWN')) obj.set('top', obj.top + amount);
          if (action.includes('LEFT')) obj.set('left', obj.left - amount);
          if (action.includes('RIGHT')) obj.set('left', obj.left + amount);
          obj.setCoords();
        });
        canvas.renderAll();
        store.pushHistory();
        return;
      }

      // 텍스트 서식 (선택 상태)
      handleTextAction(canvas, action, canvas.getActiveObject());
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canvas]);
};

export default useKeyboardShortcuts;
